// src/tests/ChildTest/NonwordRepetition/Test.js
import React, {Component} from 'react';
import strings from 'strings';
import listen from 'assets/listen.png';
import {updateStepData} from 'api';
import {OBJECT_IMAGE_MAP, SHAPE_IMAGE_MAP} from './assets';
import {
	getChildTestPack,
	getChildSayAgain,
	noDistractionsSounds,
	nnDistractionsSounds,
} from './assets';
import './styles.scss';
import AudioPlayer from 'components/AudioPlayer';
import {TYPES} from '../../constants/index';
import {playSound, stopAllSounds, createHowl} from 'utils/playSound';

// Helper to get random int inclusive
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

class Test extends Component {
	constructor(props) {
		super(props);
		const langCode = strings.getLanguage();
		const {testKeyType} = this.props;
		// Keep source URLs immutable so retakes can recreate fresh Howl instances.
		this.audioSources = getChildTestPack(langCode, testKeyType);
		this.assets = {};
		this.distractionVoiceOvers =
			langCode === 'nn' ? nnDistractionsSounds : noDistractionsSounds;
		this.sayAgainSource = getChildSayAgain(langCode); // generic "say it again"
		this.sayAgain = null;
		this.distractionTypeSequence =
			this.buildDistractionTypeSequence(testKeyType);
		this.state = {
			isInDelay: false, // true during the 15s distraction window
			distraction: null, // { type, ...payload }
			isPlayingAudio: false,
		};
	}

	componentDidMount() {
		this.initializeAudio();
	}

	componentWillUnmount() {
		this.stopSounds();
		clearTimeout(this.sayAgainTimeout);
	}

	componentDidUpdate(prevProps) {
		// New item: play new nonword
		if (prevProps.currentStep !== this.props.currentStep) {
			this.startNewTask(this.props.currentStep);
			return;
		}

		// Same item – watch for admin having scored the immediate response
		const prevSD = prevProps.stepData || {};
		const nextSD = this.props.stepData || {};

		if (nextSD.nonwordImmediateDone && !prevSD.nonwordImmediateDone) {
			// Start 10s distraction, then play "si ordet en gang til"
			this.scheduleDelayedCue();
		}
	}

	buildDistractionTypeSequence = (testKeyType) => {
		const isChild = testKeyType === TYPES.CHILD_FOLLOWUP ? true : false;
		const youthAdultbaseTypes = [
			'NAME_OBJECTS',
			'COLOR_CIRCLES',
			'NUMBER_LIST',
			'NAME_OBJECTS',
			'COLOR_CIRCLES',
			'NUMBER_LIST',
			'NAME_OBJECTS',
			'COLOR_CIRCLES',
			'NUMBER_LIST',
			'NAME_OBJECTS',
			'COLOR_CIRCLES',
			'NUMBER_LIST',
		];
		const childbaseTypes = [
			'NAME_OBJECTS',
			'COLOR_CIRCLES',
			'NUMBER_LIST',
			'CLAP_COUNT',
			'NAME_OBJECTS',
			'COLOR_CIRCLES',
			'NUMBER_LIST',
			'CLAP_COUNT',
			'NAME_OBJECTS',
			'COLOR_CIRCLES',
		];

		return isChild ? childbaseTypes : youthAdultbaseTypes;
	};

	getDistractionTypeForStep = (stepIndex) => {
		const seq = this.distractionTypeSequence;
		if (!seq || !seq.length) return 'NAME_ANIMALS'; // fallback
		

		// If there ever are more than 16 items, we just wrap around
		return seq[stepIndex % seq.length];
	};

	initializeAudio = () => {
		// Rebuild playable audio from immutable sources instead of mutating the source pack.
		this.assets = Object.keys(this.audioSources || {}).reduce((acc, key) => {
			const src = this.audioSources[key];
			const isHowl = src && typeof src.play === 'function';

			acc[key] = isHowl
				? src
				: createHowl({
						src: [src],
						preload: true,
						html5: false, // IMPORTANT for iPad
				  });
			return acc;
		}, {});

		if (this.sayAgainSource) {
			this.sayAgain = createHowl({
				src: [this.sayAgainSource],
				preload: true,
				html5: false,
			});
		}

		setTimeout(() => this.startNewTask(this.props.currentStep), 500);
	};

	startNewTask = (idx) => {
		this.stopSounds();
		clearTimeout(this.sayAgainTimeout);

		// New item → clear admin flag & distraction
		updateStepData({nonwordImmediateDone: false});

		this.setState({
			isInDelay: false,
			distraction: null,
		});

		const sound = this.assets[idx];

		if (!sound) {
			console.warn('Missing audio for nonword test step', idx);
			return;
		}

		this.setState({isPlayingAudio: true});

		playSound(sound, {
			onend: () => {
				this.setState({isPlayingAudio: false});
			},
		});

		// We now wait for admin to score the immediate response.
		// When that happens, componentDidUpdate -> scheduleDelayedCue().
	};

	// -------- distraction generation --------

	createRandomDistraction = (stepIndex) => {
		const {testKeyType, ageGroup: ag} = this.props || {};

		// Decide if this is a "Y/A" vs "C" style test from testKeyType
		const tk = String(testKeyType || '').toUpperCase();
		const isYA =
			tk.includes('Y/A') ||
			tk.includes('YOUTH') ||
			tk.includes('UNGDOM') ||
			tk.includes('ADULT') ||
			tk.includes('VOKSEN') ||
			tk.includes('YA');

		const type = this.getDistractionTypeForStep(stepIndex);
		switch (type) {
			case 'CLAP_COUNT': {
				// Task 1: Klapp hendene så mange ganger som tallet på skjermen viser (5–12)
				const n = isYA ? randInt(9, 11) : randInt(7, 9);
				return {type, n};
			}

			case 'COLOR_CIRCLES': {
				// Task 2: Si navnet på fargene du ser på skjermen
				const colors = [
					'red',
					'green',
					'blue',
					'yellow',
					'purple',
					'orange',
					'#E56FAB',
					'#7D502F',
					'black',
				];
				const count = isYA ? randInt(5, 6) : randInt(4, 5);

				const chosen = [];
				while (chosen.length < count) {
					const c = colors[randInt(0, colors.length - 1)];
					if (!chosen.includes(c)) chosen.push(c);
				}
				return {type, colors: chosen};
			}

			case 'NUMBER_LIST': {
				const isChild_2_4 =
					(tk.includes('C') ||
						tk.includes('Child') ||
						tk.includes('Barn')) &&
					(ag === 2 || ag === 3 || ag === 4);
				const isChild_5_7 =
					(tk.includes('C') ||
						tk.includes('Child') ||
						tk.includes('Barn')) &&
					(ag === 5 || ag === 6 || ag === 7);
				const isYouth =
					tk.includes('Y') ||
					tk.includes('YOUTH') ||
					tk.includes('UNGDOM');
				const isAdult =
					tk.includes('A') ||
					tk.includes('ADULT') ||
					tk.includes('VOKSEN');

				let digitCount;
				if (isChild_2_4) {
					digitCount = randInt(4, 5);
				} else if (isChild_5_7) {
					digitCount = randInt(5, 6);
				} else if (isYouth || isAdult) {
					digitCount = randInt(7, 8);
				} else {
					digitCount = randInt(4, 5);
				}

				const digits = Array.from({length: digitCount}, () =>
					randInt(0, 9)
				);

				return {type, numbers: digits};
			}

			case 'SAME_COLOR': {
				// Task 4: Nev tre gjenstander som har samme farge som skjermen viser :contentReference[oaicite:3]{index=3}
				const colors = [
					'red',
					'green',
					'blue',
					'yellow',
					'brown',
					'orange',
				];
				const color = colors[randInt(0, colors.length - 1)];
				return {type, color};
			}

			case 'NAME_ANIMALS': {
				// Task 5: Nev fire dyr så raskt du klarer (no visuals needed) :contentReference[oaicite:4]{index=4}
				return {type};
			}

			case 'NAME_OBJECTS': {
				const allLabels = Object.keys(OBJECT_IMAGE_MAP);
				const minCount = isYA ? 5 : 3;
				const maxCount = isYA ? 6 : 4;
				const count = randInt(minCount, maxCount);

				const items = [];
				while (
					items.length < count &&
					items.length < allLabels.length
				) {
					const idx = randInt(0, allLabels.length - 1);
					const label = allLabels[idx];
					if (!items.includes(label)) items.push(label);
				}
				return {type, items};
			}

			case 'SIZE_ORDER': {
				const allLabels = Object.keys(OBJECT_IMAGE_MAP);
				const count = randInt(3, Math.min(6, allLabels.length));

				const chosen = [];
				while (chosen.length < count) {
					const idx = randInt(0, allLabels.length - 1);
					const label = allLabels[idx];
					if (!chosen.includes(label)) chosen.push(label);
				}

				// Unique size ranks
				// Unique size ranks = exactly count items
				const sizeRanks = Array.from(
					{length: count},
					(_, i) => i + 1
				).sort(() => Math.random() - 0.5);

				const items = chosen.map((label, i) => ({
					label,
					sizeRank: sizeRanks[i], // always valid now
				}));

				return {type, items};
			}

			case 'SHAPE_ANALOGY': {
				const shapes = Object.keys(SHAPE_IMAGE_MAP); // ['triangle','square','circle']
				const shape = shapes[randInt(0, shapes.length - 1)];
				return {type, shape};
			}

			// Task 9 “Løs oppgaven som står på skjermen” could be added later as:
			// case 'MATH_PROBLEM': { ... }

			default:
				return {type: 'NAME_ANIMALS'};
		}
	};

	scheduleDelayedCue = () => {
		clearTimeout(this.sayAgainTimeout);

		const stepIndex = this.props.currentStep || 0;
		const distraction = this.createRandomDistraction(stepIndex);

		this.setState({
			isInDelay: true,
			distraction,
		});

		// Run distraction for 10 seconds, then ask to repeat the word
		this.sayAgainTimeout = setTimeout(() => {
			this.setState({isInDelay: false, distraction: null});

			if (!this.sayAgain) return;

			this.setState({isPlayingAudio: true});

			playSound(this.sayAgain, {
				onend: () => {
					this.setState({isPlayingAudio: false});
				},
			});
		}, 10000); // 10s
	};

	stopSounds = () => {
		stopAllSounds();
		this.setState({isPlayingAudio: false});
	};

	// -------- render helpers --------

	renderDistraction = () => {
		const {distraction} = this.state;
		if (!distraction) return null;

		switch (distraction.type) {
			case 'CLAP_COUNT':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction-audio">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction1}
							/>
						</p>
						<div className="nw-distraction__big-number">
							{distraction.n}
						</div>
					</div>
				);

			case 'COLOR_CIRCLES':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction-audio">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction2}
							/>
						</p>
						<div className="nw-distraction__circles">
							{distraction.colors.map((c) => (
								<div
									key={c}
									className="nw-distraction__circle"
									style={{backgroundColor: c}}
								/>
							))}
						</div>
					</div>
				);

			case 'NUMBER_LIST':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction-audio">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction3}
							/>
						</p>
						<div className="nw-distraction__numbers">
							{distraction.numbers.map((n, i) => (
								<span
									key={i}
									className="nw-distraction__number"
								>
									{n}
								</span>
							))}
						</div>
					</div>
				);

			case 'SAME_COLOR':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction-audio">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction6}
							/>
						</p>
						<div
							className="nw-distraction__color-block"
							style={{backgroundColor: distraction.color}}
						/>
					</div>
				);

			case 'NAME_OBJECTS':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction-audio">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction7}
							/>
						</p>
						<div className="nw-distraction__objects nw-distraction__objects--images">
							{distraction.items.map((label, idx) => {
								const src = OBJECT_IMAGE_MAP[label];
								if (!src) return null; // should not happen anymore
								return (
									<div
										key={`${label}-${idx}`}
										className="nw-distraction__object-imageWrapper"
									>
										<img
											src={src}
											alt={label}
											className="nw-distraction__object-image"
										/>
									</div>
								);
							})}
						</div>
					</div>
				);

			case 'SIZE_ORDER':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction-audio">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction8}
							/>
						</p>
						<div className="nw-distraction__objects nw-distraction__objects--images">
							{distraction.items.map((item, idx) => {
								const src = OBJECT_IMAGE_MAP[item.label];
								if (!src) return null;

								const size = 50 + item.sizeRank * 20; // random sizes (from unique sizeRank)

								return (
									<img
										key={`${item.label}-${idx}`}
										src={src}
										alt={item.label}
										style={{
											width: `${size}px`,
											height: `${size}px`,
											objectFit: 'contain',
											borderRadius: '12px',
										}}
									/>
								);
							})}
						</div>
					</div>
				);
			case 'SHAPE_ANALOGY':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction-audio">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction4}
							/>
						</p>
						<div className="nw-distraction__shape-wrapper">
							{(() => {
								const src = SHAPE_IMAGE_MAP[distraction.shape];
								if (!src) return null;
								return (
									<img
										src={src}
										alt={distraction.shape}
										className="nw-distraction__shape-image"
									/>
								);
							})()}
						</div>
					</div>
				);

			case 'NAME_ANIMALS':
			default:
				return (
					<div className="nw-distraction">
						<p className="nw-distraction-audio">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction5}
							/>
						</p>
					</div>
				);
		}
	};

	render() {
		const {isInDelay, isPlayingAudio} = this.state;
		return (
			<div className="NonwordRepetition">
				{isPlayingAudio && (
					<img className="listen-image" src={listen} alt="" />
				)}

				{/* Normal instruction text when NOT in the 10s distraction */}
				{!isInDelay && <p></p>}

				{/* 10s distraction between immediate and delayed repetition */}
				{isInDelay && this.renderDistraction()}
			</div>
		);
	}
}

export default Test;
